import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const getAttentionReference = (sample) => {
  let summary = sample?.RiepilogoCampionatura ?? sample?.riepilogo_campionatura;
  if (typeof summary === 'string') {
    try {
      summary = JSON.parse(summary);
    } catch {
      summary = null;
    }
  }

  const shippingDetails = sample?.shippingDetails
    || sample?.shipping_details
    || sample?.customer?.shippingDetails
    || sample?.customer?.shipping_details
    || summary?.customer?.shippingDetails
    || summary?.customer?.shipping_details;
  const firstName = sample?.NomeCampionatura
    || sample?.nomeCampionatura
    || sample?.nome_campionatura
    || shippingDetails?.name;
  const lastName = sample?.CognomeCampionatura
    || sample?.cognomeCampionatura
    || sample?.cognome_campionatura
    || shippingDetails?.surname;

  return [firstName, lastName]
    .map((name) => (typeof name === 'string' ? name.trim() : ''))
    .filter(Boolean)
    .join(' ');
};

export const getSampleUserName = (sample) => {
  const customerName = [sample?.NomeCliente, sample?.CognomeCliente]
    .map((name) => (typeof name === 'string' ? name.trim() : ''))
    .filter(Boolean)
    .join(' ');
  if (customerName) return customerName;

  const companyName = [sample?.business_name, sample?.Azienda]
    .find((name) => typeof name === 'string'
      && name.trim()
      && name.trim().toLocaleLowerCase('it-IT') !== 'indirizzo principale');

  return companyName?.trim() || '-';
};

export default function SamplesList({ samples }) {
  const { t } = useTranslation();

  // Funzione per parsare i prodotti e calcolare i totali
  const parseProductsData = (sample) => {
    try {
      // Prova a parsare RiepilogoCampionatura se esiste (webapp)
      if (sample?.RiepilogoCampionatura) {
        const riepilogo = typeof sample.RiepilogoCampionatura === 'string' 
          ? JSON.parse(sample.RiepilogoCampionatura) 
          : sample.RiepilogoCampionatura;
        
        return {
          items: riepilogo?.items || [],
          totalItems: riepilogo?.totale_items || 0,
          totalQuantity: riepilogo?.totale_quantita || 0,
          shippingAddress: riepilogo?.indirizzo_spedizione || null,
        };
      }
      
      // Se esiste cart_items, è un ordine dal database del sito
      if (sample?.cart_items && Array.isArray(sample.cart_items)) {
        const totalQuantity = sample.cart_items.reduce((sum, item) => sum + (parseFloat(item?.quantity) || 0), 0);
        
        return {
          items: sample.cart_items,
          totalItems: sample.cart_items.length,
          totalQuantity,
          shippingAddress: null, // L'indirizzo sarà preso da shipping_address separatamente
        };
      }
      
      // Prova a parsare il campo prodotti (webapp fallback)
      if (sample?.prodotti) {
        const prodotti = typeof sample.prodotti === 'string' 
          ? JSON.parse(sample.prodotti) 
          : sample.prodotti;
        
        const items = Array.isArray(prodotti) ? prodotti : [];
        const totalQuantity = items.reduce((sum, item) => sum + (parseFloat(item?.quantity) || 0), 0);
        
        return {
          items,
          totalItems: items.length,
          totalQuantity,
          shippingAddress: null,
        };
      }
      
      // Fallback generico
      return {
        items: sample?.items || [],
        totalItems: sample?.numero_prodotti || sample?.items?.length || 0,
        totalQuantity: sample?.quantita_articoli || 0,
        shippingAddress: sample?.shipping_address || null,
      };
    } catch (error) {
      console.error('Error parsing products data:', error, sample);
      return {
        items: [],
        totalItems: 0,
        totalQuantity: 0,
        shippingAddress: null,
      };
    }
  };

  // Funzione per tradurre lo stato
  const translateStatus = (status) => {
    if (!status) return '-';

    switch (status.toLowerCase()) {
      case 'sent':
        return t('Inviato');
      case 'draft':
        return t('In Bozza');
      default:
        return status; 
    }
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      // Se è già in formato dd-mm-yyyy (varchar), restituiscilo così com'è
      if (dateString.includes('-') && dateString.length === 10) {
        const parts = dateString.split('-');
        // Se è yyyy-mm-dd, convertilo in dd-mm-yyyy
        if (parts[0].length === 4) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        // Se è già dd-mm-yyyy, restituiscilo così
        return dateString;
      }

      // Se è un timestamp (created_at), parsalo e formattalo
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Se non è una data valida, restituisci il valore originale

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // In caso di errore, restituisci il valore originale
    }
  };

  return (
    <div className="content_data" id="samples-table">
      <div className="order_table_header">
        <div className="order_table_row">
          <div className="order_table_title sku">{t('order_number')}</div>
          <div className="order_table_title data">{t('date')}</div>
          <div className="order_table_title utente">{t('User')}</div>
          <div className="order_table_title spedizione">{t('common.shipping')}</div>
          <div className="order_table_title consegna">{t('common.reference')}</div>
          <div className="order_table_title articoli">{t('common.total_items')}</div>
          <div className="order_table_title quantita">{t('common.total_qty')}</div>
          <div className="order_table_title actions"></div>
        </div>
      </div>
      <div className="order_table_body">
        {samples.length === 0 ? (
          <div className="no-data">{t('Nessuna campionatura disponibile')}</div>
        ) : (
          samples.map((s, idx) => {
            // Parse dei dati prodotti
            const parsedData = parseProductsData(s);
            
            // Indirizzo di spedizione - priorità corretta per entrambe le API
            let shippingAddress = '-';
            if (parsedData.shippingAddress) {
              // Da RiepilogoCampionatura (webapp)
              const addr = parsedData.shippingAddress;
              shippingAddress = `${addr.addressName || ''} - ${addr.street || ''} ${addr.number || ''}, ${addr.city || ''} ${addr.province || ''} ${addr.zip || ''}`.trim();
            } else if (s?.shipping_address?.ADDRESS) {
              // Da get-order-sample-db-site (database sito)
              shippingAddress = s.shipping_address.ADDRESS;
            } else if (s?.Indirizzo || s?.Citta) {
              // Campi separati (webapp fallback)
              shippingAddress = `${s?.Azienda || s?.NomeCliente || ''} - ${s?.Indirizzo || ''} ${s?.NumeroCivico || ''}, ${s?.Citta || ''} ${s?.Provincia || ''} ${s?.CodicePostale || ''}`.trim();
            } else if (s?.indirizzo_spedizione_completo) {
              // Ulteriore fallback
              shippingAddress = s.indirizzo_spedizione_completo;
            }
            
            const totalItems = parsedData.totalItems;
            const totalQuantity = parsedData.totalQuantity;
            
            // Utente che ha richiesto la campionatura
            const userName = getSampleUserName(s);

            // Mantiene l'agente come riferimento; usa il contatto Digitex se manca.
            const attentionName = getAttentionReference(s);
            const agentName = typeof s?.agentName === 'string' ? s.agentName.trim() : '';
            const referent = agentName || attentionName || s?.sampling_customer_ref || s?.referent || '-';

            return (
              <div key={s?.id ?? idx} className="order_table_row">
                <div className="order_table_column sku" data-label={t('order_number')}>
                  <Link to={`/sample-detail/${s?.OrderIdAX ?? s?.id}`}>
                    {s?.OrderIdAX ?? s?.id ?? '-'}
                  </Link>
                </div>
                <div className="order_table_column data" data-label={t('date')}>
                  {formatDate(s?.DataOrdine ?? s?.created_at)}
                </div>
                <div className="order_table_column utente" data-label={t('User')}>
                  {userName}
                </div>
                <div className="order_table_column spedizione" data-label={t('common.shipping')}>
                  <div className="sub_data">{shippingAddress}</div>
                </div>
                <div className="order_table_column consegna" data-label={t('common.reference')}>
                  <div className="sub_data">{referent}</div>
                </div>
                <div className="order_table_column articoli" data-label={t('common.total_items')}>
                  {totalItems}
                </div>
                <div className="order_table_column quantita" data-label={t('common.total_qty')}>
                  {totalQuantity}
                </div>
                <div className="order_table_column actions">
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
